<template>
    <LoadingCard v-if="loading" />
    <div v-else>
        <div class="toolbar">
            <div class="toolbar__label">
                Chemicals available for use in spray diaries
            </div>
            <div class="actions">
                <i class="actions__item zmdi zmdi-search" @click="enableSearch"></i>
                <i class="actions__item far fa-check-square" v-if="onlySelected" @click="onlySelected=false"
                   v-b-tooltip="'Showing only selected items'"></i>
                <i class="actions__item far fa-square" v-else @click="onlySelected=true"
                   v-b-tooltip="'Show only selected items'"></i>
            </div>

            <div class="toolbar__search" :class="searchEnabled ? 'd-block' : 'd-none'">
                <input type="text" placeholder="Search..." ref="searchInput" v-model="searchTerm">
                <i class="toolbar__search__close zmdi zmdi-long-arrow-left" @click="disableSearch"></i>
            </div>
        </div>

        <div class="row">
            <div v-for="classification in filteredData" class="col-12">
                <div class="card">
                    <div class="card-header bg-primary text-white">
                        {{ classification.name}}
                    </div>

                    <b-table small hover stacked="md" :items="classification.commercialVariants" :fields="table.fields"
                             sort-by="name" :show-empty="true">
                        <template slot="activeIngredients" slot-scope="row">
                            <span class="badge-group mr-2" v-for="ingredient in [...row.item.activeIngredients].sort((a,b) => a.activeIngredient.name.localeCompare(b.activeIngredient.name))">
                                <span class="badge badge-secondary mb-1">{{ ingredient.activeIngredient.name }}</span>
                                <span class="badge badge-warning mb-1">{{ ingredient.ingredient_percentage }}%</span>
                            </span>
                        </template>

                        <template slot="stock_on_hand" slot-scope="row">
                            <template v-if="stockOnHand(row.item.id)">
                                {{ stockOnHand(row.item.id) | number_format }}
                            </template>
                            <span class="text-muted" v-else>(none)</span>
                        </template>

                        <template slot="crop_types" slot-scope="row">
                            <template v-if="registeredCropTypes(row.item)">
                                <span class="badge badge-light mb-1 mr-1" v-for="cropTypeLink in registeredCropTypes(row.item)">
                                    {{ cropTypeLink.cropType.name }}
                                </span>
                            </template>
                            <span class="text-muted" v-else>(none)</span>
                        </template>

                        <template slot="name" slot-scope="row">
                            <div class="custom-control custom-checkbox">
                                <input class="custom-control-input" :id="`chemical${row.item.id}`" type="checkbox"
                                       :checked="chemicalInUse(row.item.id)" @click.prevent="handleChemicalToggle(row.item)">
                                <label class="custom-control-label" :for="`chemical${row.item.id}`">&nbsp;</label>{{ row.item.name }}
                            </div>
                        </template>
                    </b-table>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import createLinkMutation from '@views/StockManagement/ChemicalManager/mutationCreateGrowerChemicalCommercialVariantLink.graphql';
import deleteLinkMutation from '@views/StockManagement/ChemicalManager/mutationDeleteGrowerChemicalCommercialVariantLink.graphql';
import listChemicalsQuery from '@views/StockManagement/ChemicalManager/queryListChemicalClassifications.graphql';
import listLinkedChemicalsQuery from '@views/StockManagement/ChemicalManager/queryListGrowerLinkedChemicalCommercialVariants.graphql';

/**
 * @property {String} searchTerm
 */
export default {
  name: 'ChemicalCommercialVariantManager',
  apollo: {
    allChemicals: {
      query: listChemicalsQuery,
      update: data => data.filterChemicalClassifications.data,
    },
    linkedChemicals: {
      query: listLinkedChemicalsQuery,
      update: data => data.filterGrowerChemicalCommercialVariantLinks,
    },
  },
  watch: {
    linkedChemicals(chemicals) {
      if (!this.chemicalsLoaded) {
        this.chemicalsLoaded = true;
        this.onlySelected = chemicals.length > 0;
      }
    },
  },
  computed: {
    table() {
      return {
        fields: [
          {
            key: 'name',
            label: 'Commercial Variant',
            sortable: true,
          },
          {
            key: 'stock_on_hand',
            label: 'Stock On Hand',
            sortable: true,
          },
          {
            key: 'crop_types',
            label: 'Registered For Use',
          },
          {
            key: 'activeIngredients',
            label: 'Active Ingredients',
          },
        ],
      };
    },
    filteredData() {
      const subjectMatchesSearch = subject => {
        return (
          (this.onlySelected === false || this.linkedChemicals.find(link => link.id === subject.id)) &&
          (this.searchTerm.length === 0 || subject.name.toLowerCase().includes(this.searchTerm.toLowerCase()))
        );
      };

      return this.allChemicals.map(classification => ({
          ...classification,
          commercialVariants: [...classification.commercialVariants.filter(subjectMatchesSearch)],
        }))
        .filter(classification => classification.commercialVariants.length > 0)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  mounted() {
  },
  data() {
    return {
      allChemicals: [],
      chemicalsLoaded: false,
      linkedChemicals: [],
      searchEnabled: false,
      searchTerm: '',
      onlySelected: true,
      loading: 0,
    };
  },
  methods: {
    enableSearch() {
      this.searchEnabled = true;
      this.$nextTick(() => this.$refs.searchInput.focus());
    },
    disableSearch() {
      this.searchTerm = '';
      this.searchEnabled = false;
    },
    toggleRow(item, index, event) {
      event.preventDefault();
      event.stopPropagation();
      this.handleChemicalToggle(item);
    },
    handleChemicalToggle(chemical) {
      if (this.chemicalInUse(chemical.id)) {
        this.unlinkChemical(chemical);
      } else {
        this.linkChemical(chemical);
      }
    },
    chemicalInUse(id) {
      return !!this.linkedChemicals.find(item => item.id === id);
    },
    stockOnHand(id) {
      if (!this.chemicalInUse(id)) {
        return 0;
      } else {
        return this.linkedChemicals.find(item => item.id === id).stock_on_hand;
      }
    },
    registeredCropTypes(chemical) {
      if (chemical.cropTypes.filter(link => link.is_registered).length === 0) {
        return false;
      }

      return chemical.cropTypes.filter(link => link.is_registered);
    },
    unlinkChemical(chemical) {
      this.$apollo.mutate({
        mutation: deleteLinkMutation,
        variables: {
          input: {
            id: chemical.id,
          },
        },
        update: (store, { data: { deleteGrowerChemicalCommercialVariantLink: unlinkedChemical } }) => {
          const data = store.readQuery({ query: listLinkedChemicalsQuery });
          const indexToRemove = data.filterGrowerChemicalCommercialVariantLinks.findIndex(
            item => item.id === unlinkedChemical.id,
          );
          if (indexToRemove !== -1) {
            data.filterGrowerChemicalCommercialVariantLinks.splice(indexToRemove, 1);
            store.writeQuery({ query: listLinkedChemicalsQuery, data });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteGrowerChemicalCommercialVariantLink: {
            __typename: 'ChemicalCommercialVariant',
            ...chemical,
          },
        },
      });
    },
    linkChemical(chemical) {
      this.$apollo.mutate({
        mutation: createLinkMutation,
        variables: {
          input: {
            id: chemical.id,
          },
        },
        update: (store, { data: { createGrowerChemicalCommercialVariantLink: linkedChemical } }) => {
          const data = store.readQuery({ query: listLinkedChemicalsQuery });
          data.filterGrowerChemicalCommercialVariantLinks.push(linkedChemical);
          store.writeQuery({ query: listLinkedChemicalsQuery, data });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createGrowerChemicalCommercialVariantLink: {
            __typename: 'ChemicalCommercialVariant',
            stock_on_hand: 0,
            ...chemical,
          },
        },
      });
    },
  },
};
</script>
